import React from "react";

//#region Charting Facilities
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, ChartDataset, Plugin, ScriptableLineSegmentContext } from "chart.js/auto";
ChartJS.register(CategoryScale);
//#endregion Charting Facilities

import * as MD from "reports/measures/MeasureDef";
import * as R from "reports/Report";
import * as RD from "reports/ReportDef";
import * as T from "reports/measures/Trajectory";

//#region Charting Facilities
const custom_canvas_background_color: Plugin<"line"> = {
    id: "custom_canvas_background_color",
    beforeDraw: (chart) => {
        const {
            ctx,
            chartArea: { left, width },
            scales: { y }
        } = chart;

        ctx.save();
        ctx.fillStyle = "rgba(0, 255, 0, 0.2)";
        ctx.fillRect(left, y.getPixelForValue(100), width, y.getPixelForValue(75) - y.getPixelForValue(100));
        ctx.fillStyle = "rgba(255, 165, 0, 0.2)";
        ctx.fillRect(left, y.getPixelForValue(75), width, y.getPixelForValue(50) - y.getPixelForValue(75));
        ctx.fillRect(left, y.getPixelForValue(50), width, y.getPixelForValue(25) - y.getPixelForValue(50));
        ctx.fillStyle = "rgba(255, 0, 0, 0.2)";
        ctx.fillRect(left, y.getPixelForValue(25), width, y.getPixelForValue(0) - y.getPixelForValue(25));
        ctx.restore();
    }
};

const custom_before_draw_plugin: Plugin<"line"> = {
    id: "custom_before_draw_plugin",
    beforeDraw(chart: ChartJS<"line">) {
        const dataset = chart.data?.datasets[0] as ChartDataset<"line", number[]>;
        const data = dataset?.data;

        if (!data || !Array.isArray(data)) {
            return;
        }

        const ctx = chart.ctx;
        const xAxis = chart.scales["x"];
        const yAxis = chart.scales["y"];

        let valueFrom: number | null = null;
        let valueFromIndex = 0;
        let xFrom: number | null = null;
        let yFrom: number | null = null;

        ctx.save();
        ctx.strokeStyle = dataset.borderColor as string;

        data.forEach((value, index) => {
            if (value != null) {
                const x = xAxis.getPixelForTick(index);
                const y = yAxis.getPixelForValue(value);

                if (valueFrom !== null && xFrom !== null && yFrom !== null) {
                    ctx.lineWidth = (dataset.borderWidth as number) ?? 2;
                    if (index - valueFromIndex > 1) {
                        ctx.setLineDash([5, 5]);
                    }
                    else {
                        ctx.setLineDash([]);
                    }
                    ctx.beginPath();
                    ctx.moveTo(xFrom, yFrom);
                    ctx.lineTo(x, y);
                    ctx.stroke();
                }

                valueFrom = value;
                valueFromIndex = index;
                xFrom = x;
                yFrom = y;
            }
        });

        ctx.restore();
    }
};
//#endregion Charting Facilities

// ---=== Utilities ===--- //

const reportWeekLabels = (reportWeek: RD.ReportWeek, currentWeek: number): string[] => {
    let labels: string[] = [];
    let maxWeek = R.isMonth6Report(reportWeek) ? currentWeek : Number(reportWeek);
    for (let i = 1; i <= maxWeek; i++) {
        labels.push(`Week ${i}`);
    }
    if (R.isMonth6Report(reportWeek)) {
        labels.push("6 Month");
    }
    return labels;
};

type NudgeItem = {
    value: number;
    index: number;
    index2: number;
};

const nudgeArray = (items: NudgeItem[]): NudgeItem[] => {
    const length = items.length;

    if (length === 2) {
        if (items[0].value === 0) {
            items[1].value += 1;
        }
        else {
            items[0].value -= 1;
        }
    }

    else if (length === 3) {
        if (items[0].value === 0) {
            items[1].value += 1;
            items[2].value += 2;
        }
        else if (items[0].value >= 99) {
            items[1].value -= 1;
            items[2].value -= 2;
        }
        else {
            items[0].value -= 1;
            items[2].value += 1;
        }
    }

    else if (length === 4) {
        if (items[0].value <= 1) {
            items[1].value += 1;
            items[2].value += 2;
            items[3].value += 3;
        }
        else if (items[0].value >= 99) {
            items[1].value -= 1;
            items[2].value -= 2;
            items[3].value -= 3;
        }
        else {
            items[0].value -= 2;
            items[1].value -= 1;
            items[3].value += 1;
        }
    }

    else if (length === 5) {
        if (items[0].value <= 1) {
            items[1].value += 1;
            items[2].value += 2;
            items[3].value += 3;
            items[4].value += 4;
        }
        else if (items[0].value >= 99) {
            items[1].value -= 1;
            items[2].value -= 2;
            items[3].value -= 3;
            items[4].value -= 4;
        }
        else {
            items[0].value -= 2;
            items[1].value -= 1;
            items[3].value += 1;
            items[4].value += 2;
        }
    }

    return items;
};

const nudgeSelectedTrajectoriesValues = (
    trajectories: T.Trajectories,
    selectedTrajectoryTypes: MD.MeasureTypes
): [T.Trajectories, number[][]] => {

    if (!selectedTrajectoryTypes || selectedTrajectoryTypes.length === 0) {
        return [null, null];
    }

    let selectedTrajectories: T.Trajectories = [];
    let selectedTrajectoriesNudgedValues: number[][] = Array(selectedTrajectoryTypes.length).fill(null).map(() => []);
    let selectedTrajectoriesNudgeConfigs: { [week: string]: { [value: number]: { value: number, index: number, index2: number }[] } } = {};

    selectedTrajectoryTypes.forEach(
        (selection: MD.MeasureType, selectionIndex: number) => {
            let mt: T.Trajectory = T.getTrajectoryOfType(selection, trajectories);
            selectedTrajectories.push(mt);
            mt.measures.forEach(
                (measure: MD.Measure, measureIndex: number) => {
                    let measureWeekStr: string = measure.week.toString();
                    let roundedValue: number = Math.round(mt.normalizedMeasures[measureIndex] * 10) / 10;
                    selectedTrajectoriesNudgeConfigs[measureWeekStr] = selectedTrajectoriesNudgeConfigs[measureWeekStr] || {};
                    selectedTrajectoriesNudgeConfigs[measureWeekStr][roundedValue] = selectedTrajectoriesNudgeConfigs[measureWeekStr][roundedValue] || [];
                    selectedTrajectoriesNudgeConfigs[measureWeekStr][roundedValue].push(
                        {
                            value: mt.normalizedMeasures[measureIndex],
                            index: selectionIndex,
                            index2: measureIndex
                        }
                    );
                }
            );
        }
    );

    Object.entries(selectedTrajectoriesNudgeConfigs).forEach(
        ([_measureWeekStr, weekMap]) => {
            Object.entries(weekMap).forEach(
                ([_roundedValueStr, nudgeItems]) => {
                    const nudged: NudgeItem[] = nudgeArray(nudgeItems);
                    nudged.forEach(
                        ({ index, value }: NudgeItem) => {
                            selectedTrajectoriesNudgedValues[index] = selectedTrajectoriesNudgedValues[index] || [];
                            selectedTrajectoriesNudgedValues[index].push(value);
                        }
                    );
                }
            );
        }
    );

    return [selectedTrajectories, selectedTrajectoriesNudgedValues];
};

export const configureGraphPoints = (
    measures: MD.Measures,
    normalizedMeasures: number[]
): (number | null)[] => {
    const weekToValue = new Map<number, number>();
    let maxWeek = 0;
    let m6Value: number | null = null;

    for (let i = 0; i < measures.length; i++) {
        const week = measures[i].week;
        const value = normalizedMeasures[i];

        if (typeof week === "number") {
            weekToValue.set(week, value);
            maxWeek = Math.max(maxWeek, week);
        }
        else if (week === MD.Month6Token) {
            m6Value = value;
        }
    }

    const valuesByWeek: (number | null)[] = [];
    for (let week = 1; week <= maxWeek; week++) {
        valuesByWeek.push(weekToValue.get(week) ?? null);
    }

    if (m6Value != null) {
        valuesByWeek.push(m6Value); // optionally append M6 at the end
    }

    return valuesByWeek;
};

const configureGraphData = (
    selectedTrajectories: T.Trajectories,
    selectedTrajectoriesNudgedValues: number[][],
    customGraphColors: string[]
): ChartDataset<"line", (number | null)[]>[] => {

    if (!selectedTrajectories) {
        return [];
    }

    return selectedTrajectories.map(
        (mt: T.Trajectory, index: number): ChartDataset<"line", (number | null)[]> => {
            const nudgedValues: number[] = selectedTrajectoriesNudgedValues[index];
            const dataset: (number | null)[] = configureGraphPoints(mt.measures, nudgedValues);

            return {
                label: mt.measureName,
                data: dataset,
                fill: false,
                borderWidth: 1,
                spanGaps: true,
                borderColor: customGraphColors[index],
                backgroundColor: customGraphColors[index],
                segment: {
                    borderDash: (ctx: ScriptableLineSegmentContext): number[] => {
                        const from = dataset[ctx.p0DataIndex];
                        const to = dataset[ctx.p1DataIndex];
                        return from == null || to == null ? [10, 19] : [];
                    }
                }
            };
        }
    );
};

export const renderKeyImprovedTrajectoriesGraph = (
    chartRef: React.RefObject<ChartJS<"line", number[]>>,
    reportWeek: RD.ReportWeek,
    currentWeek: number,
    trajectories: T.Trajectories,
    selectedTrajectoryTypes: MD.MeasureTypes,
    customGraphColors: string[]
) => {

    const [selectedTrajectories, selectedTrajectoriesNudgedValues] = nudgeSelectedTrajectoriesValues(trajectories, selectedTrajectoryTypes);

    return (
        <React.Fragment key={`key-trajectory-improvements-graph`}>
            <div className="[@media(max-width:1200px)]:w-[552px] w-[1104px] [@media(min-width:1700px)]:w-[1656px] [@media(max-width:1200px)]:h-[150px] h-[300px] [@media(min-width:1700px)]:h-[450px]">
                <Line
                    ref={chartRef}
                    data={
                        {
                            labels: reportWeekLabels(reportWeek, currentWeek),
                            datasets: configureGraphData(selectedTrajectories, selectedTrajectoriesNudgedValues, customGraphColors)
                        }
                    }
                    height={null}
                    width={null}
                    options={
                        {
                            aspectRatio: 1,
                            elements: {
                                line: {
                                    spanGaps: true
                                }
                            },
                            responsive: true,
                            maintainAspectRatio: false,
                            clip: false,
                            plugins: {
                                tooltip: {
                                    callbacks: {
                                        label: (context) => {
                                            return `${context.dataset.label}: ${Number(context.formattedValue).toFixed(1)}%`;
                                        }
                                    }
                                },
                                legend: {
                                    align: "end",
                                    labels: {
                                        usePointStyle: true,
                                        font: { size: 12 }
                                    }
                                }
                            },
                            spanGaps: true,
                            scales: {
                                x: {
                                    ticks: {
                                        font: {
                                            size: 12
                                        }
                                    }
                                },
                                y: {
                                    max: 100,
                                    beginAtZero: true,
                                    ticks: {
                                        stepSize: 25,
                                        autoSkip: false,
                                        callback: function (value, _index, _ticks) {
                                            return value + "%";
                                        },
                                        color: ["red", "orange", "orange", "green", "green"],
                                        font: { size: 12 }
                                    }
                                }
                            }
                        }
                    }
                    plugins={
                        [
                            custom_canvas_background_color,
                            custom_before_draw_plugin
                        ]
                    }
                />
            </div>
        </React.Fragment>
    );
};